import {
  type PlateEditor,
  someNode,
  useEventPlateId,
  usePlateEditorState,
  usePlateSelectors,
} from "@udecode/plate-core";
import { type BaseEditor, type BaseSelection, Editor, Text } from "slate";
import {
  getToolbarBlocks,
  getToolbarMarks,
  useOptionalMultiEditor,
  type EditorToolbarState,
} from "../../RuleAuthor/multiEditor";

type ToolbarEditor = PlateEditor & {
  prevSelection?: BaseSelection;
};

export const useToolbarEditor = (id?: string) => {
  const plateId = useEventPlateId(id);
  usePlateSelectors(plateId).value();
  usePlateSelectors(plateId).keySelection();
  const editor = usePlateEditorState(plateId);

  return editor as ToolbarEditor;
};

const getToolbarSelection = (editor?: ToolbarEditor) => editor?.selection ?? editor?.prevSelection ?? null;

export const isToolbarMarkActive = (editor: ToolbarEditor | undefined, type: string) => {
  const selection = getToolbarSelection(editor);

  if (!editor || !selection) {
    return false;
  }

  const editorMarks = Editor.marks(editor as BaseEditor);
  if (editorMarks?.[type] !== undefined) {
    return Boolean(editorMarks[type]);
  }

  try {
    for (const [node] of Editor.nodes(editor as BaseEditor, {
      at: selection,
      match: Text.isText,
    })) {
      if (Boolean((node as Text & Record<string, unknown>)[type])) {
        return true;
      }
    }
  } catch {
    return false;
  }

  return false;
};

export const isToolbarBlockActive = (editor: ToolbarEditor | undefined, type: string) => {
  const selection = getToolbarSelection(editor);

  return Boolean(editor && selection && someNode(editor, { at: selection, match: { type } }));
};

export const isToolbarListActive = (editor: ToolbarEditor | undefined, type: string) => {
  const selection = getToolbarSelection(editor);

  return Boolean(
    editor &&
      selection &&
      Editor.above(editor as BaseEditor, {
        at: selection,
        match: (node) => (node as { type?: unknown }).type === type,
      }),
  );
};

export const useToolbarMarkState = (id: string | undefined, type: string) => {
  const editor = useToolbarEditor(id);
  const toolbarState = useOptionalMultiEditor()?.toolbarState[id ?? ""];

  return {
    editor,
    active: toolbarState?.marks[type] ?? isToolbarMarkActive(editor, type),
  };
};

export const useToolbarBlockState = (id: string | undefined, type: string) => {
  const editor = useToolbarEditor(id);
  const toolbarState = useOptionalMultiEditor()?.toolbarState[id ?? ""];
  return {
    editor,
    active: toolbarState?.blocks[type] ?? isToolbarBlockActive(editor, type),
  };
};

export const useToolbarListState = (id: string | undefined, type: string) => {
  const editor = useToolbarEditor(id);
  const toolbarState = useOptionalMultiEditor()?.toolbarState[id ?? ""];
  return {
    editor,
    active: toolbarState?.blocks[type] ?? isToolbarListActive(editor, type),
  };
};

export const usePublishToolbarState = (id?: string) => {
  const context = useOptionalMultiEditor();

  return (editor: ToolbarEditor) => {
    if (!context || !id) {
      return;
    }

    const currentState = context.toolbarState[id];
    const nextState: EditorToolbarState = {
      marks: getToolbarMarks(editor),
      blocks: getToolbarBlocks(editor),
      currentElement: currentState?.currentElement ?? null,
    };

    context.setToolbarState(id, nextState);
  };
};

export const useToolbarHistoryState = (id?: string) => {
  const editor = useToolbarEditor(id) as PlateEditor & {
    history?: { undos?: unknown[]; redos?: unknown[] };
  };

  return {
    editor,
    canUndo: Boolean(editor?.history?.undos?.length),
    canRedo: Boolean(editor?.history?.redos?.length),
  };
};
